import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { get, post } from "../../services/api";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import Select from "react-select";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

function QuestionsTab() {
  const [triggerRerender, setTriggerRerender] = useState(false);
  const [questionsData, setQuestionsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [formTemplatesData, setFormTemplatesData] = useState([]);
  const [questionTypesData, setQuestionTypesData] = useState([]);
  const [selectedFormTemplate, setSelectedFormTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [questionTypesLoading, setQuestionTypesLoading] = useState(true);
  const [showOptionsSection, setShowOptionsSection] = useState(false);
  const [selectedQuestionForOptions, setSelectedQuestionForOptions] =
    useState(null);
  const [questionOptions, setQuestionOptions] = useState([]);
  const [includeOptions, setIncludeOptions] = useState(false);
  const userId = localStorage.getItem("userId");

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    control,
  } = useForm({
    defaultValues: {
      id: null,
      formTemplateId: "",
      categoryId: "",
      questionText: "",
      questionType: "",
      displayOrder: 1,
      isRequired: true,
      isActive: true,
    },
  });

  // Watch question type changes
  const selectedQuestionTypeValue = watch("questionType");
  const selectedQuestionType = questionTypesData.find(
    (qt) => qt.code === selectedQuestionTypeValue
  );
  const selectedFormTemplateId = watch("formTemplateId");

  // Fetch questions from API
  const getQuestions = async () => {
    try {
      setLoading(true);
      let url = "QAForm/questions";
      const params = new URLSearchParams();

      if (selectedFormTemplate?.value) {
        params.append("formTemplateId", selectedFormTemplate.value);
      }

      if (includeOptions) {
        params.append("includeOptions", "true");
      }

      const queryString = params.toString();
      if (queryString) {
        url = `${url}?${queryString}`;
      }

      const response = await get(url);

      if (response && Array.isArray(response)) {
        setQuestionsData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setQuestionsData(response.data);
      } else if (response && response.isSuccess === false) {
        toast.error(response.message || "Failed to load questions");
        setQuestionsData([]);
      } else {
        setQuestionsData([]);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error(error.message || "Failed to load questions");
      setQuestionsData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories from API
  const getCategories = async () => {
    try {
      setCategoriesLoading(true);
      // Adjust endpoint based on your API structure
      const response = await get("QAForm/categories");

      if (response && Array.isArray(response)) {
        setCategoriesData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setCategoriesData(response.data);
      } else if (response && response.isSuccess === false) {
        toast.error(response.message || "Failed to load categories");
        setCategoriesData([]);
      } else {
        setCategoriesData([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error(error.message || "Failed to load categories");
      setCategoriesData([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Fetch form templates from API
  const getFormTemplates = async () => {
    try {
      setTemplatesLoading(true);
      // Adjust endpoint based on your API structure
      const response = await get("QAForm/templates");

      if (response && Array.isArray(response)) {
        setFormTemplatesData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setFormTemplatesData(response.data);
      } else if (response && response.isSuccess === false) {
        toast.error(response.message || "Failed to load form templates");
        setFormTemplatesData([]);
      } else {
        setFormTemplatesData([]);
      }
    } catch (error) {
      console.error("Error fetching form templates:", error);
      toast.error(error.message || "Failed to load form templates");
      setFormTemplatesData([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Fetch question types from API
  const getQuestionTypes = async () => {
    try {
      setQuestionTypesLoading(true);
      const response = await get("QAForm/GetQuestionTypes");

      if (response && Array.isArray(response)) {
        setQuestionTypesData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setQuestionTypesData(response.data);
      } else if (response && response.isSuccess === false) {
        toast.error(response.message || "Failed to load question types");
        setQuestionTypesData([]);
      } else {
        setQuestionTypesData([]);
      }
    } catch (error) {
      console.error("Error fetching question types:", error);
      toast.error(error.message || "Failed to load question types");
      setQuestionTypesData([]);
    } finally {
      setQuestionTypesLoading(false);
    }
  };

  // Save/Update question
  const saveQuestion = async (data) => {
    setIsSubmitting(true);

    try {
      // Prepare API data
      const apiData = {
        id: data.id || 0,
        categoryId: parseInt(data.categoryId),
        questionText: data.questionText,
        formTemplateId: 1,
        questionType: data.questionType,
        createdBy: userId,
        displayOrder: parseInt(data.displayOrder),
        isRequired: data.isRequired,
        isActive: data.isActive,
        // Add other fields as needed by your API
      };

      let response;

      if (data.id) {
        // Update existing question
        response = await post(`QAForm/update_questions`, apiData);
      } else {
        // Create new question
        response = await post("QAForm/questions", apiData);
      }

      if (response && response.id) {
        toast.success(
          data.id
            ? "Question updated successfully!"
            : "Question added successfully!"
        );
        getQuestions(); // Refresh the list
        resetForm();
      } else if (
        response &&
        (response.isSuccess === true || response.status === "success")
      ) {
        toast.success(response.message || "Question saved successfully!");
        getQuestions(); // Refresh the list
        resetForm();
      } else {
        toast.error(response?.message || "Failed to save question");
      }
    } catch (error) {
      console.error("Error saving question:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save question"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit question
  const handleEdit = async (row) => {
    try {
      // Fetch full question data with options if needed
      const response = await get(`QAForm/questions/${row.id}`);

      const question = response?.data || response || row;

      reset({
        id: question.id,
        formTemplateId: question.formTemplateId?.toString() || "",
        categoryId: question.categoryId?.toString() || "",
        questionText: question.questionText,
        questionType: question.questionType,
        displayOrder: question.displayOrder || 1,
        isRequired: question.isRequired !== false,
        isActive: question.isActive !== false,
      });

      // Set selected form template
      const formTemplate = formTemplatesData.find(
        (ft) => ft.id === question.formTemplateId
      );
      if (formTemplate) {
        setSelectedFormTemplate({
          value: formTemplate.id.toString(),
          label: formTemplate.name,
        });
      }
    } catch (error) {
      console.error("Error editing question:", error);
      toast.error(error.message || "Failed to load question data");
    }
  };

  // Manage options for a question
  const handleManageOptions = (row) => {
    setSelectedQuestionForOptions(row);

    // Load options for the question if not already loaded
    if (row.options) {
      setQuestionOptions(row.options);
    } else {
      // Fetch options for this question
      fetchQuestionOptions(row.id);
    }

    setShowOptionsSection(true);
  };

  // Fetch options for a specific question
  const fetchQuestionOptions = async (questionId) => {
    try {
      const response = await get(`QAForm/question/${questionId}/options`);

      if (response && Array.isArray(response)) {
        setQuestionOptions(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setQuestionOptions(response.data);
      } else {
        setQuestionOptions([]);
      }
    } catch (error) {
      console.error("Error fetching question options:", error);
      setQuestionOptions([]);
    }
  };

  // Save options
  const saveOptions = async () => {
    if (!selectedQuestionForOptions) return;

    try {
      // Save options using bulk endpoint
      const apiData = {
        questionId: selectedQuestionForOptions.id,
        options: questionOptions.map((opt) => ({
          questionId: selectedQuestionForOptions.id,
          optionText: opt.optionText,
          optionValue: opt.optionValue,
          displayOrder: opt.displayOrder,
          scoreWeight: opt.scoreWeight,
          isActive: opt.isActive !== false,
        })),
      };

      const response = await post("QAForm/options/bulk", apiData);

      if (
        response &&
        (response.isSuccess === true || response.status === "success")
      ) {
        toast.success("Options saved successfully!");

        // Update the question in the list
        setQuestionsData((prev) =>
          prev.map((item) =>
            item.id === selectedQuestionForOptions.id
              ? { ...item, options: questionOptions }
              : item
          )
        );

        setShowOptionsSection(false);
        setSelectedQuestionForOptions(null);
        setQuestionOptions([]);
      } else {
        toast.error(response?.message || "Failed to save options");
      }
    } catch (error) {
      console.error("Error saving options:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save options"
      );
    }
  };

  // Add option field
  const addOptionField = () => {
    setQuestionOptions((prev) => [
      ...prev,
      {
        optionText: "",
        optionValue: "",
        scoreWeight: 1.0,
        displayOrder: prev.length + 1,
        isActive: true,
      },
    ]);
  };

  // Remove option field
  const removeOptionField = (index) => {
    if (questionOptions.length > 1) {
      const newOptions = questionOptions.filter((_, i) => i !== index);
      // Update display order
      const reorderedOptions = newOptions.map((opt, idx) => ({
        ...opt,
        displayOrder: idx + 1,
      }));
      setQuestionOptions(reorderedOptions);
    }
  };

  // Update option field
  const updateOptionField = (index, field, value) => {
    const newOptions = [...questionOptions];
    newOptions[index] = {
      ...newOptions[index],
      [field]:
        field === "scoreWeight" || field === "displayOrder"
          ? parseFloat(value)
          : value,
    };
    setQuestionOptions(newOptions);
  };

  // Delete question
  const handleDelete = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this question?"
    );

    if (confirmDelete) {
      try {
        const response = await post(
          `QAForm/questions/${rowId}`,
          {},
          { method: "DELETE" }
        );

        if (
          response &&
          (response.isSuccess === true ||
            response.status === "success" ||
            response === 204)
        ) {
          toast.success("Question deleted successfully!");
          getQuestions(); // Refresh the list
        } else {
          toast.error(response?.message || "Failed to delete question");
        }
      } catch (error) {
        console.error("Error deleting question:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete question"
        );
      }
    }
  };

  // Toggle status
  const handleToggleStatus = async (rowId) => {
    const question = questionsData.find((q) => q.id === rowId);
    if (!question) return;

    const confirmToggle = window.confirm(
      `Are you sure you want to ${
        question.isActive ? "deactivate" : "activate"
      } this question?`
    );

    if (confirmToggle) {
      try {
        // Update question status
        const apiData = {
          ...question,
          isActive: !question.isActive,
        };

        const response = await post(`QAForm/questions/${rowId}`, apiData);

        if (response && response.id) {
          toast.success(
            `Question ${
              question.isActive ? "deactivated" : "activated"
            } successfully!`
          );
          getQuestions(); // Refresh the list
        } else {
          toast.error(response?.message || "Failed to update question status");
        }
      } catch (error) {
        console.error("Error toggling status:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update question status"
        );
      }
    }
  };

  // Handle form template selection
  const handleFormTemplateSelect = (selectedOption) => {
    if (selectedOption) {
      setSelectedFormTemplate(selectedOption);
      setValue("formTemplateId", selectedOption.value);
    } else {
      setSelectedFormTemplate(null);
      setValue("formTemplateId", "");
    }
  };

  // Reset form
  const resetForm = () => {
    reset({
      id: null,
      formTemplateId: "",
      categoryId: "",
      questionText: "",
      questionType: "",
      displayOrder: 1,
      isRequired: true,
      isActive: true,
    });
    setSelectedFormTemplate(null);
  };

  // Input change handler
  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  // Get max display order for selected template
  const getMaxDisplayOrder = async (formTemplateId) => {
    try {
      const response = await get(
        `QAForm/questions/template/${formTemplateId}/max-order`
      );

      if (response !== undefined && response !== null) {
        const maxOrder = parseInt(response) || 0;
        setValue("displayOrder", maxOrder + 1);
      }
    } catch (error) {
      console.error("Error getting max display order:", error);
      // Use default order
      setValue("displayOrder", 1);
    }
  };

  // When form template changes, update display order
  useEffect(() => {
    if (selectedFormTemplate?.value && !getValues("id")) {
      getMaxDisplayOrder(selectedFormTemplate.value);
    }
  }, [selectedFormTemplate]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    getQuestions();
  }, [triggerRerender, selectedFormTemplate, includeOptions]);

  // Fetch all reference data on mount
  useEffect(() => {
    getCategories();
    // getFormTemplates();
    getQuestionTypes();
  }, []);

  // Export to Excel
  const exportToExcel = () => {
    const filteredData = questionsData.map((row, index) => ({
      "Sr No": index + 1,
      "Question Text": row.questionText,
      "Form Template": row.formTemplateName,
      Category: row.categoryName,
      "Question Type": row.questionType,
      "Display Order": row.displayOrder,
      Required: row.isRequired ? "Yes" : "No",
      Status: row.isActive ? "Active" : "Inactive",
      "Options Count": row.options?.length || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Questions");
    XLSX.writeFile(workbook, "Questions.xlsx");
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = selectedFormTemplate
      ? `Questions - ${selectedFormTemplate.label}`
      : "All Questions";

    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = 20;
    doc.text(title, titleX, titleY);

    const headers = [
      "Sr No",
      "Question Text",
      "Form Template",
      "Category",
      "Question Type",
      "Required",
      "Status",
    ];

    const bodyData = questionsData.map((row, index) => [
      index + 1,
      row.questionText?.length > 50
        ? row.questionText.substring(0, 50) + "..."
        : row.questionText || "-",
      row.formTemplateName || "-",
      row.categoryName || "-",
      row.questionType || "-",
      row.isRequired ? "Yes" : "No",
      row.isActive ? "Active" : "Inactive",
    ]);

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 60 },
        2: { cellWidth: 40 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
      },
    });

    const fileName = selectedFormTemplate
      ? `Questions_${selectedFormTemplate.label.replace(/\s+/g, "_")}.pdf`
      : "All_Questions.pdf";

    doc.save(fileName);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 60,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "questionText",
        header: "Question Text",
        size: 300,
        Cell: ({ row }) => (
          <div
            style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.original.questionText}
          >
            {row.original.questionText}
          </div>
        ),
      },
      {
        accessorKey: "formTemplateName",
        header: "Form Template",
        size: 150,
        Cell: ({ row }) => <span>{row.original.formTemplate.name}</span>,
      },
      {
        accessorKey: "categoryName",
        header: "Category",
        size: 120,
        Cell: ({ row }) => <span>{row.original.category.name}</span>,
      },
      {
        accessorKey: "questionType",
        header: "Type",
        size: 120,
        Cell: ({ row }) => (
          <span className="badge bg-info">{row.original.questionType}</span>
        ),
      },
      {
        accessorKey: "isRequired",
        header: "Required",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isRequired ? "bg-warning" : "bg-secondary"
            }`}
          >
            {row.original.isRequired ? "Yes" : "No"}
          </span>
        ),
      },
      {
        accessorKey: "isActive",
        header: "Status",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isActive ? "bg-success" : "bg-danger"
            }`}
          >
            {row.original.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 160,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original)}
              data-bs-toggle="tooltip"
              title="Edit Question"
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleManageOptions(row.original)}
              data-bs-toggle="tooltip"
              title="Manage Options"
              disabled={!row.original.hasOptions}
            >
              <i className="fas fa-list-check"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleToggleStatus(row.original.id)}
              data-bs-toggle="tooltip"
              title={row.original.isActive ? "Deactivate" : "Activate"}
            >
              <i
                className={`fas ${
                  row.original.isActive ? "fa-ban" : "fa-check"
                }`}
              ></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDelete(row.original.id)}
              data-bs-toggle="tooltip"
              title="Delete Question"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [questionsData]
  );

  // Initialize tooltips
  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, [questionsData]);

  return (
    <div className="row g-3">
      {/* Options Management Modal (when needed) */}
      {showOptionsSection && selectedQuestionForOptions && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Manage Options for: {selectedQuestionForOptions.questionText}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowOptionsSection(false);
                    setSelectedQuestionForOptions(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  Question Type:{" "}
                  <strong>{selectedQuestionForOptions.questionType}</strong>
                </div>

                <div className="options-container">
                  {questionOptions.map((option, index) => (
                    <div
                      key={index}
                      className="row g-2 mb-3 p-2 border rounded"
                    >
                      <div className="col-md-4">
                        <label className="form-label fs-10">Option Text*</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={option.optionText}
                          onChange={(e) =>
                            updateOptionField(
                              index,
                              "optionText",
                              e.target.value
                            )
                          }
                          placeholder="e.g., Yes"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fs-10">
                          Option Value*
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={option.optionValue}
                          onChange={(e) =>
                            updateOptionField(
                              index,
                              "optionValue",
                              e.target.value
                            )
                          }
                          placeholder="e.g., YES"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fs-10">
                          Display Order
                        </label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={option.displayOrder}
                          onChange={(e) =>
                            updateOptionField(
                              index,
                              "displayOrder",
                              e.target.value
                            )
                          }
                          min="1"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fs-10">Score Weight</label>
                        <input
                          type="number"
                          step="0.1"
                          className="form-control form-control-sm"
                          value={option.scoreWeight}
                          onChange={(e) =>
                            updateOptionField(
                              index,
                              "scoreWeight",
                              e.target.value
                            )
                          }
                          min="0"
                          max="1"
                        />
                      </div>
                      <div className="col-md-1 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeOptionField(index)}
                          disabled={questionOptions.length <= 1}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-primary btn-sm mt-2"
                    onClick={addOptionField}
                  >
                    <i className="fas fa-plus me-1"></i> Add Option
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowOptionsSection(false);
                    setSelectedQuestionForOptions(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={saveOptions}
                >
                  Save Options
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Left Side - Form */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">
              {getValues("id") ? "Edit Question" : "Add New Question"}
            </h5>
          </div>
          <div className="card-body">
            <form
              className="row g-3 new-form-style"
              onSubmit={handleSubmit(saveQuestion)}
            >
              <input type="hidden" {...register("id")} />

              <div className="col-md-12 position-relative">
                <label
                  className="form-label form-label-new"
                  style={{ zIndex: 100 }}
                >
                  Category<span className="text-danger fs-9">*</span>
                </label>
                <Controller
                  name="categoryId"
                  control={control}
                  rules={{ required: "Category is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={categoriesData.map((cat) => ({
                        value: cat.id?.toString(),
                        label: cat.name,
                        ...cat,
                      }))}
                      value={
                        categoriesData.find(
                          (cat) => cat.id?.toString() === field.value
                        )
                          ? {
                              value: field.value,
                              label: categoriesData.find(
                                (cat) => cat.id?.toString() === field.value
                              )?.name,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        field.onChange(selected ? selected.value : "")
                      }
                      placeholder="Select category..."
                      className="basic-single"
                      classNamePrefix="select"
                      isLoading={categoriesLoading}
                      styles={{
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 1050, // Lower than Bootstrap modal (1055) but higher than most elements
                        }),
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: 1050,
                        }),
                        control: (provided) => ({
                          ...provided,
                          zIndex: 1,
                        }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.categoryId && (
                  <small
                    className="text-danger position-relative"
                    style={{ zIndex: 100 }}
                  >
                    {errors.categoryId.message}
                  </small>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label form-label-new">
                  Question Text<span className="text-danger fs-9">*</span>
                </label>
                <textarea
                  {...register("questionText", {
                    required: "Question text is required.",
                    maxLength: {
                      value: 500,
                      message: "Maximum of 500 characters allowed.",
                    },
                  })}
                  className="form-control"
                  rows="3"
                  placeholder="Enter your question here"
                  onChange={handleChangeOfInputWithLengthCheck("questionText")}
                />
                {errors.questionText && (
                  <small className="text-danger">
                    {errors.questionText.message}
                  </small>
                )}
              </div>

              <div className="col-md-6 position-relative">
                <label
                  className="form-label form-label-new"
                  style={{ zIndex: 100 }}
                >
                  Question Type<span className="text-danger fs-9">*</span>
                </label>
                <Controller
                  name="questionType"
                  control={control}
                  rules={{ required: "Question type is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={questionTypesData.map((qt) => ({
                        value: qt.code,
                        label: qt.name,
                        ...qt,
                      }))}
                      value={
                        questionTypesData.find((qt) => qt.code === field.value)
                          ? {
                              value: field.value,
                              label: questionTypesData.find(
                                (qt) => qt.code === field.value
                              )?.name,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        field.onChange(selected ? selected.value : "")
                      }
                      placeholder="Select question type..."
                      className="basic-single"
                      classNamePrefix="select"
                      isLoading={questionTypesLoading}
                      styles={{
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 1050,
                        }),
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: 1050,
                        }),
                        control: (provided) => ({
                          ...provided,
                          zIndex: 1,
                        }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.questionType && (
                  <small
                    className="text-danger position-relative"
                    style={{ zIndex: 100 }}
                  >
                    {errors.questionType.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label form-label-new">
                  Display Order<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("displayOrder", {
                    required: "Display order is required.",
                    valueAsNumber: true,
                    min: { value: 1, message: "Minimum order is 1" },
                  })}
                  type="number"
                  className="form-control"
                  placeholder="1"
                  min="1"
                />
                {errors.displayOrder && (
                  <small className="text-danger">
                    {errors.displayOrder.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <div className="form-check">
                  <input
                    {...register("isRequired")}
                    className="form-check-input"
                    type="checkbox"
                    id="isRequired"
                    defaultChecked
                  />
                  <label className="form-check-label" htmlFor="isRequired">
                    Required Question
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-check">
                  <input
                    {...register("isActive")}
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    defaultChecked
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Active
                  </label>
                </div>
              </div>

              {/* Preview of selected question type */}
              {selectedQuestionType && (
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title mb-0">
                        {selectedQuestionType.name}
                      </h6>
                    </div>
                    <div className="card-body">
                      <p className="small mb-2">
                        {selectedQuestionType.description}
                      </p>
                      {selectedQuestionType.hasOptions &&
                      selectedQuestionType.defaultOptions?.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Option Text</th>
                                <th>Option Value</th>
                                <th>Score Weight</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedQuestionType.defaultOptions.map(
                                (opt, idx) => (
                                  <tr key={idx}>
                                    <td>{opt.optionText}</td>
                                    <td>
                                      <code>{opt.optionValue}</code>
                                    </td>
                                    <td>
                                      <span
                                        className={`badge ${
                                          opt.scoreWeight >= 0.7
                                            ? "bg-success"
                                            : opt.scoreWeight >= 0.4
                                            ? "bg-warning"
                                            : "bg-danger"
                                        }`}
                                      >
                                        {opt.scoreWeight}
                                      </span>
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="alert alert-info py-2 mb-0">
                          <small>
                            This question type doesn't have predefined options.
                          </small>
                        </div>
                      )}
                      <small className="text-muted">
                        {selectedQuestionType.hasOptions
                          ? "These are default options. You can customize them after saving the question."
                          : "Text input questions don't require options."}
                      </small>
                    </div>
                  </div>
                </div>
              )}

              <div className="col-12 mt-3">
                <button
                  className="btn btn-primary btn-sm"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : getValues("id")
                    ? "Update Question"
                    : "Add Question"}
                </button>

                {getValues("id") && (
                  <button
                    className="btn btn-secondary btn-sm ms-2"
                    type="button"
                    onClick={resetForm}
                  >
                    Cancel Edit
                  </button>
                )}

                <button
                  className="btn btn-outline-secondary btn-sm ms-2"
                  type="button"
                  onClick={() => {
                    resetForm();
                    toast.info("Form reset");
                  }}
                  disabled={isSubmitting}
                >
                  Reset Form
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Data Table */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="card-title mb-0">Questions</h5>
              {selectedFormTemplate && (
                <small className="text-muted">
                  Filtered by: {selectedFormTemplate.label}
                </small>
              )}
            </div>
            <div className="d-flex gap-2 align-items-center">
              {/* Include Options Toggle */}
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="includeOptions"
                  checked={includeOptions}
                  onChange={(e) => setIncludeOptions(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="includeOptions">
                  Show Options
                </label>
              </div>

              {/* Form Template Filter */}
              <div style={{ width: "200px" }}>
                <Select
                  options={[
                    { value: "", label: "All Templates" },
                    ...formTemplatesData.map((ft) => ({
                      value: ft.id?.toString(),
                      label: ft.name,
                    })),
                  ]}
                  value={
                    selectedFormTemplate
                      ? {
                          value: selectedFormTemplate.value,
                          label: selectedFormTemplate.label,
                        }
                      : { value: "", label: "All Templates" }
                  }
                  onChange={(selected) => {
                    if (selected && selected.value === "") {
                      setSelectedFormTemplate(null);
                    } else {
                      setSelectedFormTemplate(selected);
                    }
                  }}
                  placeholder="Filter by template..."
                  className="basic-single"
                  classNamePrefix="select"
                  isLoading={templatesLoading}
                />
              </div>

              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={exportToExcel}
                data-bs-toggle="tooltip"
                title="Export to Excel"
                disabled={loading || questionsData.length === 0}
              >
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={exportToPDF}
                data-bs-toggle="tooltip"
                title="Export to PDF"
                disabled={loading || questionsData.length === 0}
              >
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading questions...</p>
              </div>
            ) : questionsData.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                <p className="text-muted">
                  {selectedFormTemplate
                    ? `No questions found for template: ${selectedFormTemplate.label}`
                    : "No questions found"}
                </p>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={getQuestions}
                >
                  <i className="fas fa-sync me-1"></i> Refresh
                </button>
              </div>
            ) : (
              <div className="table-container fixed-last-column-table">
                <CustomDatatableGrid
                  columns={columns}
                  data={questionsData}
                  onExportExcel={exportToExcel}
                  onExportPDF={exportToPDF}
                  initialState={{
                    columnPinning: { right: ["actions"] },
                    columnVisibility: {
                      formTemplateName: !selectedFormTemplate,
                      questionType: false,
                    },
                    pagination: {
                      pageSize: 10,
                      pageIndex: 0,
                    },
                  }}
                  enableRowSelection={false}
                  enableColumnFilters={true}
                  enableGlobalFilter={true}
                  enablePagination={true}
                  enableSorting={true}
                  enableColumnResizing={true}
                  muiTablePaperProps={{
                    elevation: 0,
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "500px",
                    },
                  }}
                  renderTopToolbarCustomActions={() => (
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold">
                        Total: {questionsData.length}
                      </span>
                      <span className="badge bg-success">
                        Active: {questionsData.filter((q) => q.isActive).length}
                      </span>
                      <span className="badge bg-warning">
                        Required:{" "}
                        {questionsData.filter((q) => q.isRequired).length}
                      </span>
                      <span className="badge bg-info">
                        Templates:{" "}
                        {
                          [
                            ...new Set(
                              questionsData.map((q) => q.formTemplateId)
                            ),
                          ].length
                        }
                      </span>
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuestionsTab;
